"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { Mail, X, Phone, Globe, User } from "lucide-react";
import AlexLogo from "@/components/icons/AlexLogo";
import {
  useCreateLeadMutation,
  type LeadCategory,
} from "@/redux/features/lead/leadApi";
import { toast } from "sonner";
import { trackCallClicked } from "@/lib/analytics";

type BannerVariant = {
  id: number;
  headline: string;
  subtitle: string;
  image: string;
  placeholder: string;
  buttonLabel: string;
  category: LeadCategory;
};

const bannerVariants: BannerVariant[] = [
  {
    id: 1,
    headline: "Control the Anxiety",
    subtitle: "Get the free 3-3-3 Grounding Guide sent straight to your inbox.",
    image: "/images/blogs/assets/1 - Vertical.png",
    placeholder: "Enter your email...",
    buttonLabel: "Get Your Free Guide",
    category: "grounding_guide_333",
  },
  {
    id: 2,
    headline: "Find Your Peace & Reset",
    subtitle:
      "Start your healing journey with the pause & reset blueprint, sent to your inbox",
    image: "/images/blogs/assets/2 - Vertical.png",
    placeholder: "Enter your email...",
    buttonLabel: "Get Your Free Blueprint",
    category: "pause_reset_blueprint",
  },
  {
    id: 3,
    headline: "Stay Calm, Avoid Burnout",
    subtitle:
      "Get the free anti burnout weekly planner delivered straight to your inbox",
    image: "/images/blogs/assets/3 - Vertical.png",
    placeholder: "Enter your email...",
    buttonLabel: "Get Your Free Planner",
    category: "anti_burnout_weekly_planner",
  },
];

export default function SlideUpBanner() {
  const pathname = usePathname();
  const [activeVariant, setActiveVariant] = useState<BannerVariant | null>(
    null,
  );
  const [isVisible, setIsVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Lead API mutation
  const [createLead] = useCreateLeadMutation();

  const staticRoutes = [
    "/",
    "/24seven-self-discovery",
    "/about",
    "/allied-health",
    "/anger-management",
    "/booking",
    "/career-counselling",
    "/changing-habits",
    "/contact",
    "/counselling",
    "/wellness-blog",
    "/podcast",
    "/depression-anxiety",
    "/counselling-faq",
    "/focus-productivity",
    "/grief-loss",
    "/life-coach",
    "/occupational-therapy",
    "/privacy-policy",
    "/psychology",
    "/public-speaking",
    "/relationships",
    "/self-esteem-confidence",
    "/teams",
    "/terms-of-use",
    "/time-management",
  ];

  const cleanPathname = pathname ? pathname.replace(/\/$/, "") || "/" : "/";
  const isStaticRoute = staticRoutes.includes(cleanPathname);

  useEffect(() => {
    if (!isStaticRoute) return;

    // Check if dismissed in this session
    const isDismissed =
      sessionStorage.getItem("slide-banner-dismissed") === "1";
    if (isDismissed) {
      setDismissed(true);
      return;
    }

    // Get or generate variant for this session
    const storedVariant = sessionStorage.getItem("slide-banner-variant");
    let variant: BannerVariant;

    if (storedVariant) {
      const variantId = parseInt(storedVariant);
      variant =
        bannerVariants.find((v) => v.id === variantId) || bannerVariants[0];
    } else {
      const randomIndex = Math.floor(Math.random() * bannerVariants.length);
      variant = bannerVariants[randomIndex];
      sessionStorage.setItem("slide-banner-variant", variant.id.toString());
    }
    setActiveVariant(variant);

    let checkInterval: any = null;
    let hasReachedScroll = false;

    const checkAndShow = () => {
      const isPopupActive =
        document.querySelector('[data-booking-popup-open="true"]') !== null;
      if (!isPopupActive) {
        setIsVisible(true);
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
      }
    };

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const scrollPercent = scrollTop / docHeight;

      if (scrollPercent >= 0.3 && !hasReachedScroll) {
        hasReachedScroll = true;
        window.removeEventListener("scroll", handleScroll);

        checkAndShow();
        const isPopupActive =
          document.querySelector('[data-booking-popup-open="true"]') !== null;
        if (isPopupActive) {
          checkInterval = setInterval(checkAndShow, 500);
        }
      }
    };

    // Check immediately in case they are already scrolled past 30%
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (checkInterval) clearInterval(checkInterval);
    };
  }, [isStaticRoute]);

  const dismiss = () => {
    setIsVisible(false);
    // Wait for the slide-down transition to complete (1000ms) before unmounting
    setTimeout(() => {
      sessionStorage.setItem("slide-banner-dismissed", "1");
      setDismissed(true);
      setName("");
      setEmail("");
    }, 1000);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || isSubmitting || !activeVariant?.category) return;

    setIsSubmitting(true);
    try {
      await createLead({
        fullName: name,
        email,
        source: "SLIDEUP",
        category: activeVariant?.category,
      }).unwrap();
      setName("");
      setEmail("");
      toast.success("Check your inbox!");

      // Auto-close banner after short delay
      setTimeout(() => {
        dismiss();
      }, 1500);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (dismissed || !activeVariant || !isStaticRoute) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 w-full overflow-visible transition-all duration-1000 ease-out ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-full opacity-0"
      }`}
    >
      {/* Background card with grain noise gradient */}
      <div className="cta-noise-bg relative mx-auto min-h-[95px] max-w-5xl overflow-visible rounded-t-2xl border-x border-t border-white/10 px-4 pb-7 pt-3.5 shadow-2xl md:px-8 md:pb-9 md:pt-5 lg:min-h-[100px]">
        {/* Absolute portrait cutout overlapping gold bar and top edge */}
        <div className="absolute bottom-0 left-1.5 z-10 h-[135px] w-[100px] overflow-visible sm:left-2 sm:h-[165px] sm:w-[120px] md:left-3 md:h-[205px] md:w-[150px] lg:left-4 lg:h-[255px] lg:w-[185px]">
          <Image
            src={activeVariant.image}
            alt=""
            fill
            sizes="300px"
            className="pointer-events-none select-none object-contain object-bottom"
            priority
          />
        </div>

        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute right-3 top-2.5 z-20 rounded-full p-1 text-white/70 transition-colors duration-200 hover:bg-white/10 hover:text-white"
          aria-label="Close"
        >
          <X className="h-4.5 w-4.5" />
        </button>

        {/* Horizontal flex content */}
        <div className="ml-[75px] flex flex-1 flex-col gap-4 pb-3 pr-2 sm:ml-[95px] md:ml-[125px] lg:ml-[165px] lg:flex-row lg:items-center lg:justify-between lg:pr-6">
          {/* Text Info */}
          <div className="flex max-w-sm select-none flex-col justify-center">
            <h3 className="font-virtual text-[30px] leading-none text-secondary sm:text-[38px] lg:text-[50px]">
              {activeVariant.headline}
            </h3>
            <p className="mt-1 max-w-xs font-onest text-[10px] leading-snug text-white/90 md:text-xs">
              {activeVariant.subtitle}
            </p>
          </div>

          {/* Form with Name and Email in column stack */}
          <div className="z-10 flex w-full items-center lg:max-w-[280px]">
            <form
              onSubmit={handleEmailSubmit}
              className="flex w-full flex-col gap-1"
            >
              {/* Full Name field */}
              <div className="flex w-full items-center gap-2 rounded-full bg-white p-0.5 shadow-md">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#034FB6]">
                  <User className="h-3 w-3 text-white" />
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name..."
                  className="w-full bg-transparent pr-4 font-onest text-[10px] text-gray-700 placeholder-gray-400 outline-none"
                  required
                />
              </div>

              {/* Email address field */}
              <div className="flex w-full items-center gap-2 rounded-full bg-white p-0.5 shadow-md">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#034FB6]">
                  <Mail className="h-3 w-3 text-white" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={activeVariant.placeholder}
                  className="w-full bg-transparent pr-4 font-onest text-[10px] text-gray-700 placeholder-gray-400 outline-none"
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="h-6.5 flex w-full items-center justify-center rounded-full bg-[#c5ab5d] py-1 font-onest text-[10px] font-semibold text-white shadow-md transition-all duration-300 hover:scale-[1.01] hover:bg-[#b0964d] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? "Submitting..." : activeVariant.buttonLabel}
              </button>
            </form>
          </div>

          {/* Watermark Logo (Single Line SVG Signature) */}
          <div className="hidden shrink-0 select-none items-center justify-end pl-4 xl:flex">
            <div className="flex flex-col items-center gap-1">
              <AlexLogo className="h-6 w-auto fill-white text-white" />
              <div className="h-[1.5px] w-[140px] bg-white/70" />
            </div>
          </div>
        </div>

        {/* Bottom Gold Bar */}
        <div className="absolute bottom-0 left-0 right-0 z-0 flex h-8 items-center justify-center gap-6 bg-[#DEC068] px-4 text-[9px] font-semibold text-[#02386D] sm:text-[10px]">
          <a
            href="tel:+611300467513"
            className="flex items-center gap-1.5 transition-colors duration-150 hover:underline"
            aria-label="Call 1300467513"
            onClick={() => trackCallClicked({ location: "slide_up_banner" })}
          >
            <Phone className="h-4 w-4 sm:h-3 sm:w-3" fill="currentColor" />
            <span className="hidden sm:inline">1300 467 513</span>
          </a>
          <a
            href="https://www.alexrodriguez.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 transition-colors duration-150 hover:underline"
            aria-label="Visit www.alexrodriguez.com.au"
          >
            <Globe className="h-4 w-4 sm:h-3 sm:w-3" />
            <span className="hidden sm:inline">www.alexrodriguez.com.au</span>
          </a>
        </div>
      </div>
    </div>
  );
}
